import 'what-input/dist/what-input';
import Vue, { CreateElement, VNode } from 'vue';
import { getModule as getStoreModule } from 'vuex-module-decorators';
import { captureAndLogError, initSentry } from '@madebyheyday/sentry-util';
import PresentationStoreModule from '@app/store/modules/PresentationStoreModule';
import transformSlide from '@app/functions/transformSlide';
import GlobalsStoreModule from '@app/store/modules/GlobalsStoreModule';
import UserStoreModule from '@app/store/modules/UserStoreModule';
import App from './App.vue';
import { getRouter } from './router';
import store from './store/store';
import loadFonts from './functions/loadFonts';
import GlobalsPlugin from './plugins/GlobalsPlugin';
import './head.static';
import './head-async.static';
import '@assets/css/main.scss';
import AuthStoreModule from './store/modules/AuthStoreModule';
import AppStoreModule from './store/modules/AppStoreModule';
import registerServiceWorker from './registerServiceWorker';

Vue.config.productionTip = false;

if (process.env.SENTRY_ENVIRONMENT !== 'local') {
	initSentry({ environment: process.env.SENTRY_ENVIRONMENT, release: process.env.RELEASE_VERSION });
}

// If we're in offline mode, register service worker so it may intersect requests
if (!navigator.onLine) {
	registerServiceWorker();
}

// init JIRA issue collector:
(window as any).ATL_JQ_PAGE_PROPS = {
	triggerFunction(showCollectorDialog: any): void {
		(window as any).showCollectorDialog = showCollectorDialog;
	},
	fieldValues: {
		description: `--> Add description & steps for reproduction here <--\n\nVersion: ${process.env
			.DEPLOYMENT_VERSION as string}\nBranch: ${process.env.DEPLOYMENT_BRANCH}`
	}
};

// scroll restauration will mess with back navigation during share chapter navigation,
// so we will handle back button scroll restauration ourselves
if ('scrollRestoration' in window.history) {
	window.history.scrollRestoration = 'manual';
}

Vue.use(GlobalsPlugin);

loadFonts();

const authStore: AuthStoreModule = getStoreModule(AuthStoreModule);
const appStore: AppStoreModule = getStoreModule(AppStoreModule);
const globalsStore: GlobalsStoreModule = getStoreModule(GlobalsStoreModule);
const presentationStore: PresentationStoreModule = getStoreModule(PresentationStoreModule);
const userStore: UserStoreModule = getStoreModule(UserStoreModule);

// Set slide transform function from outside the store to avoid cycle dependencies when transforms using the store
presentationStore.setSlideTransformFunction(transformSlide);

// register stores in window object to allow for debugging
(window as any).authStore = authStore;
(window as any).appStore = appStore;
(window as any).globalsStore = globalsStore;
(window as any).presentationStore = presentationStore;
(window as any).userStore = userStore;

if ('storage' in navigator && 'estimate' in navigator.storage) {
	appStore.setSupportsCaching(true);
}

async function executeApp(): Promise<void> {
	console.info(`[App] Version: ${process.env.DEPLOYMENT_VERSION}`);
	// management token needs to be restored before navigation guards kick in
	await authStore.restoreStateIfSupported();

	const router = await getRouter();
	new Vue({
		router,
		store,
		render: (h: CreateElement): VNode => h(App)
	}).$mount('#app');
}

executeApp().catch((error: Error): void => {
	try {
		appStore.captureError(error);
	} catch (errorDuringCapture) {
		captureAndLogError(error);
		if (error instanceof Error) {
			captureAndLogError(errorDuringCapture as Error);
		}
	}
});
